/**
 * The Autodesk daily report when no single project is chosen.
 *
 * - Every project in the workspace is on Autodesk: "Led AI - Daily Report
 *   Editor"'s all-projects screen — one row per project for the day, opening
 *   that project's report.
 * - Autodesk beside Procore or unlinked projects: the day stays the shared
 *   walk timeline, and an Autodesk walk collapses to one line naming its
 *   report, where it stands, and what is in it.
 */
import type { CSSProperties } from "react";
import { ChevronRight, Circle, CircleAlert, CircleCheck, CircleDot, RefreshCw } from "lucide-react";

import type { AutodeskDailyLogMappingPlan } from "../../../../backend/src/shared/autodeskFormTemplate";
import type { ConsoleRow, ConsoleSite, DailyLogAutodeskTemplate, DailyLogWalkRow } from "../api";
import { initialsOf } from "../components/chrome";
import { ADR, C, FONT_MONO } from "../tokens";
import { AutodeskGlyph, AutodeskStyles } from "./AutodeskDailyReport";

const mono: CSSProperties = { fontFamily: FONT_MONO };

const SHORT: Record<string, string> = { Manpower: "Manpower", Deliveries: "Deliv", Equipment: "Equip" };

/** "3 MANPOWER · 2 DELIV · 1 EQUIP · 4 EVENTS" — what the accepted records put
 *  on the Form, by where they land. Issues are not report entries. */
export function autodeskReportCounts(plan: AutodeskDailyLogMappingPlan, rows: ConsoleRow[]): string {
  // Ordered as the Form reads: its tables in layout order, then the template
  // questions answered, then notes.
  const order = [...plan.sections].sort((a, b) => a.displayIndex - b.displayIndex)
    .flatMap((section) => (plan.tableBindings ?? []).filter((binding) => binding.sectionId === section.id).map((binding) => binding.sectionLabel));
  const counts = new Map<string, number>();
  const add = (label: string) => counts.set(label, (counts.get(label) ?? 0) + 1);
  for (const row of rows) {
    if (row.reviewStatus !== "accepted" || row.recordType === "observation" || row.category === "observations") continue;
    const table = plan.tableBindings?.find((binding) => binding.sourceCategory === row.category);
    if (table) { add(table.sectionLabel); continue; }
    const question = plan.questionBindings?.find((binding) => binding.sourceCategory === row.category && binding.mode !== "manual");
    add(question ? "Events" : "Notes");
  }
  const rank = (label: string) => {
    const index = order.indexOf(label);
    return index >= 0 ? index : label === "Events" ? order.length : order.length + 1;
  };
  const noun = (label: string, count: number) => {
    if (label === "Events") return count === 1 ? "event" : "events";
    if (label === "Notes") return count === 1 ? "note" : "notes";
    return SHORT[label] ?? label;
  };
  return [...counts].sort((a, b) => rank(a[0]) - rank(b[0])).map(([label, count]) => `${count} ${noun(label, count)}`).join(" · ");
}

type Status = { label: string; tone: "ok" | "wait" | "sync" | "fail" | "none" };

function statusOf(template: DailyLogAutodeskTemplate | undefined, pending: number): Status {
  if (pending > 0) return { label: "Needs review", tone: "wait" };
  if (!template?.formId) return { label: "No report", tone: "none" };
  if (template.syncStatus === "pending") return { label: "Syncing", tone: "sync" };
  if (template.syncStatus === "failed") return { label: "Sync failed", tone: "fail" };
  return { label: "In Autodesk", tone: "ok" };
}

function StatusLabel({ status }: { status: Status }) {
  const tone = {
    ok: { fg: ADR.green, icon: <CircleCheck size={13} color={ADR.green} aria-hidden /> },
    wait: { fg: ADR.amberInk, icon: <CircleDot size={13} color={ADR.amberInk} aria-hidden /> },
    sync: { fg: ADR.blueInk, icon: <RefreshCw size={13} color={ADR.blueInk} aria-hidden /> },
    fail: { fg: ADR.redInk, icon: <CircleAlert size={13} color={ADR.redInk} aria-hidden /> },
    none: { fg: ADR.faintRule, icon: <Circle size={13} color={ADR.dot} aria-hidden /> },
  }[status.tone];
  return (
    <span data-testid="autodesk-report-state" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 600, color: tone.fg, whiteSpace: "nowrap" }}>
      {tone.icon}{status.label}
    </span>
  );
}

function shortTime(iso?: string): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const hours = date.getHours();
  return `${hours % 12 || 12}:${String(date.getMinutes()).padStart(2, "0")}${hours < 12 ? "a" : "p"}`;
}

function plural(count: number, one: string, many: string) {
  return `${count} ${count === 1 ? one : many}`;
}

export interface AutodeskDayReportsProps {
  /** "Friday, October 17" */
  dayLabel: string;
  /** "Oct 17" */
  dayShort: string;
  sites: ConsoleSite[];
  walks: DailyLogWalkRow[];
  rows: ConsoleRow[];
  hrefForSite: (siteId: string) => string;
  onNavigate: (href: string) => void;
}

/** Every Autodesk project's report for the day, one row each. */
export function AutodeskDayReports(props: AutodeskDayReportsProps) {
  const entries = props.sites.map((site) => {
    const rows = props.rows.filter((row) => row.jobSiteId === site.id);
    const visitIds = new Set(rows.map((row) => row.visitId));
    const walks = props.walks
      .filter((walk) => visitIds.has(walk.visitId))
      .sort((a, b) => Date.parse(a.startedAt ?? "") - Date.parse(b.startedAt ?? ""));
    const template = walks.map((walk) => walk.autodeskTemplate).find((candidate) => candidate?.formId);
    const pending = rows.filter((row) => row.reviewStatus === "pending").length;
    return { site, rows, walks, template, pending, status: statusOf(template, pending) };
  });
  // Projects with something on the day first, then the quiet ones, by name.
  entries.sort((a, b) =>
    Number(b.walks.length > 0) - Number(a.walks.length > 0) ||
    (a.site.name || a.site.id).localeCompare(b.site.name || b.site.id));
  const reports = entries.filter((entry) => entry.template).length;
  const waiting = entries.filter((entry) => entry.pending > 0).length;
  const meta = [plural(reports, "report", "reports"), waiting > 0 ? `${waiting} needs review` : null].filter(Boolean).join(" · ");

  return (
    <div data-testid="autodesk-day-reports">
      <AutodeskStyles />
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 16, marginTop: 22 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, letterSpacing: "-.6px" }}>{props.dayLabel}</h1>
        <span style={{ ...mono, fontSize: 9.5, fontWeight: 600, letterSpacing: "1.1px", color: C.sub2, textTransform: "uppercase" }}>{meta}</span>
      </div>
      <div style={{ marginTop: 12, border: "1px solid rgba(20,17,12,.08)", borderRadius: 14, background: ADR.card, overflow: "hidden" }}>
        {entries.map(({ site, rows, walks, template, status }, index) => {
          const active = walks.length > 0;
          const first = walks[0];
          const name = template?.overrides?.name ?? template?.formName ?? (active ? `Daily report — ${props.dayShort}` : "No report");
          const sub = active
            ? `From ${first?.name || "Untitled walk"}${first?.startedAt ? ` · ${shortTime(first.startedAt)}` : ""}${walks.length > 1 ? ` · +${walks.length - 1} more` : ""}`
            : `No walks recorded on ${props.dayShort}`;
          const pendingCount = rows.filter((row) => row.reviewStatus === "pending").length;
          const counts = template ? autodeskReportCounts(template.plan, rows) : pendingCount > 0 ? plural(pendingCount, "record waiting", "records waiting") : "";
          const href = props.hrefForSite(site.id);
          const body = (
            <>
              <span style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
                <span style={{ opacity: active ? 1 : 0.35, display: "inline-flex" }}><AutodeskGlyph size={14} /></span>
                <span style={{ fontSize: 14, fontWeight: 700, letterSpacing: "-.2px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: active ? C.ink : C.sub2 }}>{site.name || "Untitled project"}</span>
              </span>
              <span style={{ minWidth: 0 }}>
                <span style={{ display: "block", fontSize: 13.5, fontWeight: active ? 600 : 500, color: active ? C.ink : C.sub2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{name}</span>
                <span style={{ display: "block", fontSize: 12, color: ADR.label, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{sub}</span>
              </span>
              <StatusLabel status={status} />
              <span style={{ ...mono, fontSize: 9.5, fontWeight: 600, letterSpacing: ".8px", color: C.sub2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textTransform: "uppercase" }}>{counts || "—"}</span>
              {active && first?.walkerName ? (
                <span aria-label={first.walkerName} title={first.walkerName} style={{ width: 28, height: 28, borderRadius: 14, background: C.track, color: C.ink, display: "grid", placeItems: "center", fontSize: 10.5, fontWeight: 800 }}>{initialsOf(first.walkerName)}</span>
              ) : <span />}
              {active ? <ChevronRight size={14} color={C.sub2} aria-hidden /> : <span />}
            </>
          );
          const rowStyle: CSSProperties = {
            display: "grid", gridTemplateColumns: "minmax(0,200px) minmax(0,1fr) 130px minmax(0,230px) 28px 16px", alignItems: "center", gap: 14,
            width: "100%", padding: "14px 18px", borderTop: index === 0 ? "none" : "1px solid rgba(20,17,12,.06)", background: "transparent", textAlign: "left", textDecoration: "none", color: "inherit", boxSizing: "border-box",
          };
          return active ? (
            <a key={site.id} href={href} className="adr-hov" data-testid="autodesk-day-report-row" onClick={(event) => { event.preventDefault(); props.onNavigate(href); }} style={{ ...rowStyle, cursor: "pointer" }}>
              {body}
            </a>
          ) : (
            <div key={site.id} data-testid="autodesk-day-report-row" style={rowStyle}>{body}</div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * An Autodesk walk on a mixed timeline: one line, and the report is a click
 * away. Its entries are the report's, so they are not listed again here.
 */
export function AutodeskReportStrip({ template, rows, href, onNavigate }: {
  template: DailyLogAutodeskTemplate;
  rows: ConsoleRow[];
  href: string;
  onNavigate: (href: string) => void;
}) {
  const pending = rows.filter((row) => row.reviewStatus === "pending").length;
  const counts = autodeskReportCounts(template.plan, rows);
  const name = template.overrides?.name ?? template.formName ?? "Daily report";
  return (
    <>
    <AutodeskStyles />
    <a
      href={href}
      data-testid="autodesk-report-strip"
      className="adr-hov"
      onClick={(event) => { event.preventDefault(); onNavigate(href); }}
      style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 18px", textDecoration: "none", color: C.ink, minWidth: 0 }}
    >
      <AutodeskGlyph size={14} />
      <span style={{ flex: "0 1 auto", minWidth: 0, fontSize: 13.5, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{name}</span>
      <StatusLabel status={statusOf(template, pending)} />
      <span style={{ flex: 1, minWidth: 0, ...mono, fontSize: 9.5, fontWeight: 600, letterSpacing: ".8px", color: C.sub2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textTransform: "uppercase" }}>{counts}</span>
      <ChevronRight size={14} color={C.sub2} aria-hidden />
    </a>
    </>
  );
}
